import { uid } from '../../../../lib/uid';
import type { StorageFacilityOption } from '../../../logistics/inventory/inventoryTypes';
import type { RequestableItem, RequisitionSlipLineInput } from '../requisitionSlipTypes';

/**
 * A plausible requisition slip, filled from the LIVE feeds.
 *
 * **It takes the catalogs as an argument**, because a parameterless generator cannot pick a
 * real row and silently fills none of the foreign keys — which is how a generated form comes
 * out looking complete and is refused on save.
 *
 * **Nothing it produces reaches `rsys_pii`.** A purpose and a note are prose about supplies,
 * and the accountable employee is deliberately NOT generated: that picker names a real person,
 * and a generator choosing one would put an employee's name against a slip somebody else
 * raised. It is left empty, which the server permits.
 */
export function sampleRequisitionSlip({
  items,
  facilities,
  staff,
}: {
  items: RequestableItem[];
  facilities: StorageFacilityOption[];

  /*
    THE LIVE PERSONNEL FEED. "The primary action enables" is the FLOOR — the accountable
    employee is OPTIONAL, and filling it is what makes a generated slip exercise the field
    phase 2's issuance bridge reads. It is empty for a reader without `personnel.read`, whose
    form does not draw the control either.
  */
  staff: { id: string }[];
}): {
  storageFacilityId: string | null;
  accountablePersonnelId: string | null;
  purpose: string;
  notes: string;
  items: RequisitionSlipLineInput[];
} {
  const purposes = [
    'Replenishing the counter stock the office ran down this month',
    'Preparing the materials the quarterly audit walkthrough needs',
    'Replacing supplies used up during the relocation',
    'Stocking the new workstations before the intake starts',
  ];

  /*
    THE SUFFIX is what makes a second click produce something new. Without it a reader pressing
    Autogenerate twice gets the same sentence, which reads as the button having failed.
  */
  const purpose = `${pick(purposes)} (${uid().slice(0, 6)})`;

  /*
    REAL ROWS FROM THE LIVE FEED, never an invented id — one the server does not hold is
    refused, which reads as a bug in the form rather than in the generator. Zero-stock items
    are deliberately eligible: asking for what is not there is what the module is for.
  */
  const lines: RequisitionSlipLineInput[] = sample(items, 3).map((item) => ({
    inventoryItemId: item.id,
    requestedQuantity: 1 + Math.floor(Math.random() * 12),
    remarks: null,
  }));

  return {
    storageFacilityId: facilities.length > 0 ? pick(facilities).id : null,
    accountablePersonnelId: staff.length > 0 ? pick(staff).id : null,
    purpose,
    notes: 'Generated sample — please verify before submitting.',
    items: lines,
  };
}

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Up to `count` distinct rows, or everything there is when the feed is shorter. */
function sample<T>(values: readonly T[], count: number): T[] {
  const pool = [...values];
  const taken: T[] = [];

  while (pool.length > 0 && taken.length < count) {
    taken.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }

  return taken;
}
